import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EmptyStateHandlingState, EmptyStateConfig, EmptyStateContext } from '@/types/emptyState';

const initialState: EmptyStateHandlingState = {
  isEmptyState: false,
  isRegenerating: false,
  config: {
    showGenerateButton: true,
    regenerateOnEmpty: false,
    emptyStateMessage: "You've seen all available conversation starters!",
    buttonText: "Generate More",
    showStats: true,
    autoRetryCount: 0,
  },
  context: {
    hasSwipedCards: false,
    totalCardsShown: 0,
    swipeStats: {
      left: 0,
      right: 0,
      total: 0,
    },
    canRegenerate: true,
  },
  error: null,
  retryAttempts: 0,
  maxRetryAttempts: 3,
};

const emptyStateSlice = createSlice({
  name: 'emptyState',
  initialState,
  reducers: {
    enterEmptyState: (state, action: PayloadAction<EmptyStateContext>) => {
      state.isEmptyState = true;
      state.context = { ...state.context, ...action.payload };
      state.error = null;
    },

    exitEmptyState: (state) => {
      state.isEmptyState = false;
      state.isRegenerating = false;
      state.error = null;
      state.retryAttempts = 0;
    },

    startRegeneration: (state) => {
      state.isRegenerating = true;
      state.error = null;
    },

    completeRegeneration: (state) => {
      state.isRegenerating = false;
      state.isEmptyState = false;
      state.error = null;
      state.retryAttempts = 0;
    },

    failRegeneration: (state, action: PayloadAction<string>) => {
      state.isRegenerating = false;
      state.error = action.payload;
      state.retryAttempts += 1;

      // If we've exceeded max retries, disable regeneration
      if (state.retryAttempts >= state.maxRetryAttempts) {
        state.context.canRegenerate = false;
      }
    },

    updateConfig: (state, action: PayloadAction<Partial<EmptyStateConfig>>) => {
      state.config = { ...state.config, ...action.payload };
    },

    updateContext: (state, action: PayloadAction<Partial<EmptyStateContext>>) => {
      state.context = { ...state.context, ...action.payload };
    },

    updateSwipeStats: (state, action: PayloadAction<{ left: number; right: number; total: number }>) => {
      state.context.swipeStats = action.payload;
      state.context.hasSwipedCards = action.payload.total > 0;
    },

    resetRetryAttempts: (state) => {
      state.retryAttempts = 0;
      state.context.canRegenerate = true;
      state.error = null;
    },

    setLastGenerationTime: (state, action: PayloadAction<string>) => {
      state.context.lastGenerationTime = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  enterEmptyState,
  exitEmptyState,
  startRegeneration,
  completeRegeneration,
  failRegeneration,
  updateConfig,
  updateContext,
  updateSwipeStats,
  resetRetryAttempts,
  setLastGenerationTime,
  clearError,
} = emptyStateSlice.actions;

export default emptyStateSlice.reducer;